/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import Model from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import { guidFor } from '@ember/object/internals';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { debounceTask } from 'ember-lifeline';
import { format } from 'date-fns/format';
import { startOfDay } from 'date-fns/startOfDay';
import { endOfDay } from 'date-fns/endOfDay';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import { addMinutes } from 'date-fns/addMinutes';
import { isAfter } from 'date-fns/isAfter';
import { addDays } from 'date-fns/addDays';
import { dasherize } from '@ember/string';
import { waitFor } from '@ember/test-waiters';
import {
  BookingStatus,
  SafeguestVerificationStatus,
  type ISafeguestVerification,
} from 'uplisting-frontend/models/booking';
import { utcDate, isBetweenDays } from 'uplisting-frontend/utils/date';
import { AnalyticsEvents } from 'uplisting-frontend/services/analytics';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import {
  getEditDetailsRouteFor,
  getSendMessageRouteFor,
} from 'uplisting-frontend/utils/rhs/routes';

export interface IBookingAction {
  enabled: boolean;
  isCompleted: boolean;
  translationKey: string;
  classNames: string;
  onApply(): void;
  icon: string;
}

interface IArgs {
  booking: ModelRegistry['booking'];
}

export interface BookingSummarySignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export function timeOptionToTime(option: string): string {
  const isAm = option.includes('am');
  const [hours, minutes] = option
    .replaceAll(/pm|am/gi, '')
    .split(':')
    .map(Number) as [number, number];

  let newHours = isAm ? hours : hours + 12;

  if (isAm && newHours === 12) {
    newHours = 0;
  } else if (!isAm && newHours === 24) {
    newHours = 12;
  }

  const date = utcDate(new Date());

  date.setHours(newHours, minutes, 0);

  return format(date, 'H:mm:ss');
}

export default class UiBookingSummaryV1Component extends Component<BookingSummarySignature> {
  @service features!: Services['features'];
  @service store!: Services['store'];
  @service router!: Services['router'];
  @service session!: Services['session'];
  @service analytics!: Services['analytics'];
  @service notifications!: Services['notifications'];
  @service sentry!: Services['sentry'];
  @service('repositories/account-configuration')
  accountConfigurationRepository!: Services['repositories/account-configuration'];

  timeFormat = 'h:mmaaa';

  @cached
  get booking(): ModelRegistry['booking'] {
    return this.args.booking;
  }

  @cached
  get id(): string {
    return guidFor(this);
  }

  @cached
  get currentUser(): ModelRegistry['user'] {
    return this.session.currentUser as ModelRegistry['user'];
  }

  @cached
  get editDetailsRoute(): string {
    return getEditDetailsRouteFor(this.router.currentRouteName ?? '');
  }

  @cached
  get sendMessageRoute(): string {
    return getSendMessageRouteFor(this.router.currentRouteName ?? '');
  }

  @cached
  get canMessageGuest(): boolean {
    return this.currentUser.hasAccessTo('bookings.messages');
  }

  @cached
  get showBookingPaymentStatus() {
    if (this.booking.isIcal || this.booking.isPending) {
      return false;
    }

    return true;
  }

  @cached
  get showGuestIdentityVerificationStatus(): boolean {
    return !!this.booking.guestIdentityVerification;
  }

  @cached
  get guestIdentityVerification(): ModelRegistry['guest-identity-verification'] {
    return this.booking
      .guestIdentityVerification as ModelRegistry['guest-identity-verification'];
  }

  @cached
  get showSafeguestVerificationStatus(): boolean {
    return !!this.safeguestVerificationStatus;
  }

  @cached
  get safeguestVerificationStatus(): SafeguestVerificationStatus | undefined {
    return this.booking.safeguestVerification?.status;
  }

  @cached
  get safeguestVerification(): ISafeguestVerification {
    return this.booking.safeguestVerification as ISafeguestVerification;
  }

  @cached
  get showLockCodeStatus(): boolean {
    return !!this.booking.lockCode;
  }

  @cached
  get lockCode(): ModelRegistry['lock-code'] {
    return this.booking.lockCode as ModelRegistry['lock-code'];
  }

  @cached
  get showSecurityDepositStatus(): boolean {
    return !!this.booking.securityDeposit;
  }

  @cached
  get showDisputes(): boolean {
    return (
      this.features.isProtectionGateEnabled &&
      this.currentUser.hasAccessTo('disputes.index') &&
      this.booking.disputes.length !== 0
    );
  }

  @cached
  get showOverrideSecurityDeposit(): boolean {
    const { securityDepositAction } = this.booking;
    const isSettled =
      !!securityDepositAction?.isSettle && !securityDepositAction.isNew;

    return this.showSecurityDepositStatus && !isSettled;
  }

  @cached
  get showSettleSecurityDeposit(): boolean {
    const { securityDeposit } = this.booking;

    return !!securityDeposit && securityDeposit.isSettleable;
  }

  @cached
  get showOverrideRentalAgreement(): boolean {
    return (
      !!this.booking.bookingRentalAgreement &&
      !this.booking.bookingRentalAgreement.isSigned
    );
  }

  @cached
  get showResetRentalAgreement(): boolean {
    return (
      !!this.booking.bookingRentalAgreement &&
      this.booking.bookingRentalAgreement.isSigned
    );
  }

  @cached
  get timeOptions(): string[] {
    const start = startOfDay(new Date());
    const end = endOfDay(new Date());
    const step = 15; // in minutes
    const diff = differenceInMinutes(end, start);
    const stepsCount = Math.ceil(diff / step);

    return Array(stepsCount)
      .fill(undefined)
      .map((_, i) => format(addMinutes(start, i * step), this.timeFormat));
  }

  @cached
  get bookingActions(): IBookingAction[] {
    const {
      booking: {
        isCheckedIn,
        isCheckedOut,
        needsCheckIn,
        needsCheckOut,
        isCheckOutToday,
        pendingAlterationRequest,
      },
    } = this;

    const isNeedsCheckOutEnabled =
      (needsCheckOut || isCheckOutToday) && !isCheckedOut;

    const actions = [
      {
        enabled: needsCheckIn,
        isCompleted: isCheckedIn || isCheckedOut,
        classNames: 'booking-check-in',
        translationKey: 'action_bookings_summary.actions.check_guest_in',
        onApply: () => {
          if (!needsCheckIn) {
            return;
          }

          this.updateStatus(BookingStatus.checkedIn);
        },
        get icon(): string {
          return this.enabled ? 'exclamation-circle' : 'clock';
        },
      },
      {
        enabled: isNeedsCheckOutEnabled,
        isCompleted: isCheckedOut,
        classNames: 'booking-check-out',
        translationKey: 'action_bookings_summary.actions.check_guest_out',
        onApply: () => {
          if (!isNeedsCheckOutEnabled) {
            return;
          }

          this.updateStatus(BookingStatus.checkedOut);
        },
        get icon(): string {
          return this.enabled ? 'exclamation-circle' : 'clock';
        },
      },
    ];

    if (this.booking.canBeModified) {
      actions.push({
        enabled: !pendingAlterationRequest,
        isCompleted: !!pendingAlterationRequest,
        classNames: 'booking-alteration-request',
        translationKey: 'action_bookings_summary.actions.alteration_request',
        onApply: () => {
          if (pendingAlterationRequest) {
            return;
          }

          this.router.transitionTo(
            `${this.baseRoute}.bookings.summary.modify`,
            this.booking.id,
          );
        },
        get icon(): string {
          return this.enabled ? 'pen' : 'clock';
        },
      });
    }

    return actions;
  }

  @cached
  get automatedReviewsEnabled(): boolean {
    return this.accountConfigurationRepository.currentAccountConfiguration
      .automatedReviewsEnabled;
  }

  @cached
  get hasAutomatedReviews(): boolean {
    return this.automatedReviewsEnabled && this.booking.isAirbnbOfficial;
  }

  @cached
  get canToggleAutomatedReviews(): boolean {
    return this.automatedReviewsEnabled && !this.booking.automatedReviewSent;
  }

  @cached
  get showReportCancellationByOwner(): boolean {
    const { booking } = this;

    return (
      this.currentUser.hasAccessTo('calendar.bookings.cancel') &&
      booking.isVrbo &&
      isAfter(booking.checkOut, new Date())
    );
  }

  @cached
  get showReportCancellationByTraveller(): boolean {
    return this.showReportCancellationByOwner;
  }

  @cached
  get showReportInvalidCard(): boolean {
    const { booking } = this;

    if (!booking.isBookingDotCom) {
      return false;
    }

    return isAfter(booking.checkIn, new Date());
  }

  @cached
  get showReportNoShow(): boolean {
    const { booking } = this;

    if (!booking.isBookingDotCom) {
      return false;
    }

    const { checkInDate } = booking;

    return isBetweenDays(
      new Date(),
      addDays(checkInDate, 1),
      addDays(checkInDate, 2),
    );
  }

  @cached
  get showBookingPaymentMarkAsPaid(): boolean {
    const { booking } = this;

    if (booking.isPending || !booking.isPayable) {
      return false;
    }

    if (!booking.bookingPayment) {
      return true;
    }

    return booking.bookingPayment.canBeMarkedAsPaid;
  }

  @cached
  get hasOtherActions(): boolean {
    const actionFields = [
      'showReportCancellationByOwner',
      'showReportCancellationByTraveller',
      'showReportInvalidCard',
      'showReportNoShow',
      'showOverrideSecurityDeposit',
      'showSettleSecurityDeposit',
      'showBookingPaymentMarkAsPaid',
      'showOverrideRentalAgreement',
      'showGuestIdentityVerificationStatus',
    ];

    return actionFields.some((field) => this[field]);
  }

  @cached
  get showCustomLockCodeInput(): boolean {
    return this.booking.lockCodeEditable;
  }

  @cached
  get customLockCode(): string {
    const { booking } = this;

    return booking.customLockCode || booking.lockCode?.pin || '';
  }

  @cached
  get acceptRoute(): string {
    return (this.router.currentRouteName ?? '').includes('action')
      ? 'action.bookings.summary.accept'
      : 'xcalendar.bookings.summary.accept';
  }

  @cached
  get baseRoute(): string {
    const currentRouteName = this.router.currentRouteName ?? '';

    if (currentRouteName.startsWith('action')) {
      return 'action';
    } else if (currentRouteName.startsWith('calendar')) {
      return 'calendar';
    }

    return 'xcalendar';
  }

  @action
  handleSaveExpiresIn(event: Event): void {
    debounceTask(this, 'saveExpiresIn', event, 500);
  }

  @action
  @waitFor
  async handleMarkBookingAsConfirmed(): Promise<void> {
    await this.handleGenericSave<ModelRegistry['booking-action']>(
      'bookingAction',
      'action_bookings_summary.mark_as_confirmed.notifications.applied',
      { confirmPending: true },
      undefined,
      true,
    );
  }

  @action
  @waitFor
  async handleSelectTime(
    field: 'arrivalTime' | 'departureTime',
    option: string,
  ): Promise<void> {
    this.booking[field] = timeOptionToTime(option);

    await this.booking.save();
  }

  @action
  @waitFor
  async handleToggleAutomatedMessages(): Promise<void> {
    await this.saveBookingNow({
      automatedMessagesEnabled: !this.booking.automatedMessagesEnabled,
    });
  }

  @action
  @waitFor
  async handleToggleAutomatedReviews(): Promise<void> {
    const { booking } = this;

    await this.saveBookingNow({
      automatedReviewsEnabled: !booking.automatedReviewsEnabled,
    });

    const event = booking.automatedReviewsEnabled
      ? AnalyticsEvents.bookingAutomatedReviewsEnabled
      : AnalyticsEvents.bookingAutomatedReviewsDisabled;

    this.analytics.trackEvent(event);
  }

  @action
  @waitFor
  async handleMarkBookingDotComAsNoShowAction(): Promise<void> {
    await this.handleGenericSave<ModelRegistry['booking-dot-com-action']>(
      'bookingDotComAction',
      'action_bookings_summary.other_actions.booking_dot_com_no_show.notifications.applied',
      { noShow: true },
      { noShow: false },
    );
  }

  @action
  @waitFor
  async handleBookingDotComInvalidCard(): Promise<void> {
    await this.handleGenericSave<ModelRegistry['booking-dot-com-action']>(
      'bookingDotComAction',
      'action_bookings_summary.other_actions.booking_dot_com_invalid_card.notifications.applied',
      { invalidCreditCard: true },
      { invalidCreditCard: false },
    );
  }

  @action
  @waitFor
  async handleVrboCancelBy(type: 'owner' | 'traveller'): Promise<void> {
    const record =
      this.getRecordFor<ModelRegistry['homeaway-action']>('homeawayAction');

    const field =
      type === 'owner' ? 'cancelledByOwner' : 'cancelledByTraveller';

    record[field] = true;

    try {
      await record.save();

      await record.booking.reload();

      this.notifications.info(
        'action_bookings_summary.other_actions.vrbo_cancellation_by_owner.notifications.applied',
      );

      this.router.transitionTo(this.baseRoute);
    } catch {
      record[field] = false;

      this.notifications.error();
    }
  }

  @action
  @waitFor
  async handleOverrideSecurityDepositAction(): Promise<void> {
    await this.handleGenericSave<ModelRegistry['security-deposit-action']>(
      'securityDepositAction',
      'action_bookings_summary.other_actions.security_deposit.notifications.applied',
      { overridden: true },
      { overridden: false },
    );
  }

  @action
  @waitFor
  async handleMarkBookingPaymentActionAsPaid(): Promise<void> {
    await this.handleGenericSave<ModelRegistry['booking-payment-action']>(
      'bookingPaymentAction',
      'action_bookings_summary.other_actions.booking_payment.notifications.applied',
      { markedAsPaid: true },
      { markedAsPaid: false },
      true,
    );
  }

  @action
  @waitFor
  async handleMarkBookingRentalAgreementAsSigned(): Promise<void> {
    await this.handleGenericSave<
      ModelRegistry['booking-rental-agreement-action']
    >(
      'bookingRentalAgreementAction',
      'action_bookings_summary.other_actions.rental_agreement_sign.notifications.applied',
      { reset: false, markedAsSigned: true },
      { reset: false, markedAsSigned: false },
      true,
    );
  }

  @action
  @waitFor
  async handleMarkBookingRentalAgreementAsReset(): Promise<void> {
    await this.handleGenericSave<
      ModelRegistry['booking-rental-agreement-action']
    >(
      'bookingRentalAgreementAction',
      'action_bookings_summary.other_actions.rental_agreement_reset.notifications.applied',
      { reset: true, markedAsSigned: false },
      { reset: false, markedAsSigned: false },
      true,
    );
  }

  @action
  @waitFor
  async handleMarkGuestIdentityVerificationAsNotNeeded(): Promise<void> {
    await this.handleGenericSave<
      ModelRegistry['guest-identity-verification-action']
    >(
      'guestIdentityVerificationAction',
      'action_bookings_summary.other_actions.guest_identity_verification.notifications.applied',
      { overridden: true },
      { overridden: false },
      true,
    );
  }

  @action
  handleInputLockCode(event: Event): void {
    const { value } = event.target as HTMLInputElement;

    this.booking.customLockCode = value;
  }

  @action
  @waitFor
  async handleSaveCustomLockCode(): Promise<void> {
    try {
      await this.booking.save();

      this.notifications.info(
        'action_bookings_summary.lock_code.notifications.applied',
      );

      this.booking.customLockCode = null;
    } catch {
      this.notifications.error();
    }
  }

  private async saveExpiresIn(event: Event): Promise<void> {
    const { value } = event.target as HTMLInputElement;

    if (isEmpty(value)) {
      return;
    }

    this.booking.expiresIn = Number(value);

    await this.booking.save();

    this.notifications.info('action_bookings_summary.expires_in.success');
  }

  private async updateStatus(status: BookingStatus) {
    await this.saveBookingNow({ status });
  }

  protected async saveBookingNow(
    attributes: Partial<ModelRegistry['booking']>,
  ): Promise<void> {
    Object.assign(this.booking, attributes);

    await this.booking.save();
  }

  private async handleGenericSave<T extends Model>(
    relationship: string,
    notificationKey: string,
    updateData: object,
    resetData?: object,
    reloadBooking?: boolean,
  ): Promise<void> {
    const record = this.getRecordFor<T>(relationship);

    Object.assign(record, updateData);

    try {
      await record.save();

      this.notifications.info(notificationKey);

      if (reloadBooking) {
        await this.booking.reload();
      }
    } catch {
      this.notifications.error();

      if (resetData) {
        Object.assign(record, resetData);
      }
    }
  }

  private getRecordFor<T>(relationship: string): T {
    const { store, booking } = this;

    const id = booking[relationship]?.id;
    const modelName = dasherize(relationship);

    if (id) {
      return store.peekRecord(modelName, id);
    }

    return store.createRecord(modelName, {
      booking,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Summary::V1': typeof UiBookingSummaryV1Component;
  }
}
